/**
 * useMobileCheckoutReturn Hook
 *
 * Detects when the user returns from a Stripe checkout on mobile
 * (same-page redirect) and triggers the appropriate callbacks
 */

import { useEffect } from 'react';
import { useToast } from '@/shared/hooks/use-toast';
import { useSubscription } from '@/features/billing/contexts/SubscriptionContext';

export function useMobileCheckoutReturn() {
  const { toast } = useToast();
  const { refreshSubscription } = useSubscription();

  useEffect(() => {
    // Check if we just returned from a Stripe checkout
    const checkoutCompleted = sessionStorage.getItem('checkout_completed');

    if (!checkoutCompleted) {
      return; // Not returning from checkout
    }

    console.log('[MobileCheckoutReturn] Detected return from Stripe:', checkoutCompleted);

    // Clear the flag immediately to prevent duplicate handling
    sessionStorage.removeItem('checkout_completed');

    // Handle success
    if (checkoutCompleted === 'success') {
      const sessionId = sessionStorage.getItem('checkout_session_id');
      sessionStorage.removeItem('checkout_session_id');

      console.log('[MobileCheckoutReturn] ✅ Payment successful! Session:', sessionId);

      // Show success toast
      toast({
        title: "Payment Successful!",
        description: "Your subscription is now active. Thank you!",
      });

      // Refresh subscription data
      refreshSubscription().catch((error) => {
        console.error('[MobileCheckoutReturn] Error refreshing subscription:', error);
      });
    }

    // Handle error
    else if (checkoutCompleted === 'error') {
      const errorMessage = sessionStorage.getItem('checkout_error_message') || 'An error occurred during payment';
      sessionStorage.removeItem('checkout_error_message');

      console.log('[MobileCheckoutReturn] ❌ Payment failed:', errorMessage);

      // Show error toast
      toast({
        title: "Payment Failed",
        description: errorMessage,
        variant: "destructive",
      });

      // Still refresh to ensure state is accurate
      refreshSubscription().catch((error) => {
        console.error('[MobileCheckoutReturn] Error refreshing subscription:', error);
      });
    }
  }, [toast, refreshSubscription]);
}
